import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _noop from 'lodash/noop';

import { Tracking } from 'bdvp-tracking';

import { useMainContainerQuery } from '../../hooks';
import { useInAppView } from '../../hooks/config';

import { videoFeedTypes } from '../../utils';
import { SEARCH_ROUTE } from '../../utils/constants';

import Share from './Share';
import Title from './Title';
import Description from './Description';
import DateDuration from './DateDuration';
import Tags from './Tags';
import ToggleButton from './ToggleButton';
import Collapsible from './Collapsible';
import ReelButton from './ReelButton';
import ShareReelWrapper from './ShareReelWrapper';
import UserCreated from './UserCreated';
import GameMatchup from './GameMatchup';
import PlayerMatchup from './PlayerMatchup';

const { REEL } = videoFeedTypes;

const Metadata = ({
    mediaPlayback,
    searchRoute,
    onMetadataExpand,
    onTagClick,
    userInfo,
}) => {

    const [
        showPlaybackTags = true,
        showReelsShareWrapper = true,
        showShareButton = true,
        showToggleInReelButton = true,
    ] = useInAppView([
        'playbackTags',
        'playbackReelsShare',
        'playbackReelsShareToggleInReelButton',
        'playbackReelsShareButton',
    ]);

    const [
        collapse,
        setCollapse,
    ] = useState(true);

    const onMetadataButtonClick = useCallback(() => {

        if (collapse) {

            onMetadataExpand();

        }

        setCollapse(!collapse);

    }, [
        collapse,
        onMetadataExpand,
        setCollapse,
    ]);

    const isTagsBelowPlayerMatchup = useMainContainerQuery(
        ['MD'],
        true,
        false
    );

    const slug = _get(mediaPlayback, 'slug', '');
    const isInReel = _get(mediaPlayback, 'isInReel', false);
    const description = _get(mediaPlayback, 'description');
    const feedType = mediaPlayback?.type;
    const isCollapsible = mediaPlayback.keywordsDisplay?.length > 0 || description;

    const tagsComponent = showPlaybackTags ? (
        <Tags
            mediaPlayback={mediaPlayback}
            searchRoute={searchRoute}
            onTagClick={onTagClick}
        />
    ) : null;

    return (
        <>
            {isCollapsible
                && <ToggleButton
                    ariaLabel={collapse ? 'Expand Metadata' : 'Collapse Metadata'}
                    size={'sm'}
                    iconName={collapse ? 'arrow-expand' : 'arrow-contract'}
                    handlers={
                        {
                            onClick: (event) => {

                                event.preventDefault();
                                onMetadataButtonClick();

                            },
                        }
                    }
                />
            }
            <Title>
                {_get(mediaPlayback, 'title')}
            </Title>
            <GameMatchup
                mediaPlayback={mediaPlayback}
                searchRoute={searchRoute}
                onClick={() => {

                    Tracking.trackGameMatchupClick();

                }}
            />
            <DateDuration
                date={_get(mediaPlayback, 'date')}
                duration={_get(mediaPlayback, 'duration')}
            />
            {showReelsShareWrapper
                && <ShareReelWrapper>
                    {feedType !== REEL && showToggleInReelButton && (
                        <ReelButton
                            mediaPlayback={mediaPlayback}
                            onClick={isInReel ? Tracking.trackRemoveFromReelButtonClick : Tracking.trackAddToReelButtonClick}
                            disabled={!(_get(mediaPlayback, 'canAddToReel', true))}
                        />
                    )}
                    {showShareButton && (
                        <Share
                            mediaId={_get(mediaPlayback, 'id', '')}
                            onClick={() => {

                                Tracking.onShare({
                                    type: 'Share',
                                    slug,
                                });

                            }}
                        />
                    )}
                </ShareReelWrapper>
            }
            {isCollapsible && (
                <Collapsible
                    collapse={collapse}
                >
                    <Description>
                        {description}
                    </Description>
                    {!isTagsBelowPlayerMatchup && tagsComponent}
                </Collapsible>
            )}
            <PlayerMatchup
                mediaPlayback={mediaPlayback}
                searchRoute={searchRoute}
            />
            {feedType === REEL && (
                <UserCreated
                    userInfo={userInfo}
                    onLinkClick={() => {

                        Tracking.trackFanCreatedLinkClick();

                    }}
                />
            )}
            {isTagsBelowPlayerMatchup && tagsComponent}

        </>
    );

};

Metadata.propTypes = {
    mediaPlayback: PropTypes.object,
    searchRoute: PropTypes.string,
    onMetadataExpand: PropTypes.func,
    onTagClick: PropTypes.func,
    userInfo: PropTypes.object,
};

Metadata.defaultProps = {
    mediaPlayback: {},
    searchRoute: SEARCH_ROUTE,
    onMetadataExpand: _noop,
    onTagClick: _noop,
    userInfo: {},
};

export default Metadata;
